import { useCallback, useEffect, useRef, useState } from "react";

import type { KoryHaloState } from "@/components/kory/kory-halo";
import type { LiveTurn } from "@/components/overview/cos-morning/cos-live-voice";
import { sharedAudioContext, soundsEnabled } from "@/lib/kory-sounds";
import { stripMarkdown } from "@/lib/sop-summary";
import { formatTranscript } from "@/lib/voice-live/voice-copy";

import type { CosSessionMessage } from "./types";

/** A message typed to Kory, found by its words and which occurrence of them it is; `stopped` once you stop it. */
export type TypedAsk = { text: string; prior: number; stopped?: boolean };

export type TypedTurnView = {
  turn: LiveTurn;
  /** Where your line sits in the thread: everything before it is the earlier conversation. */
  at: number;
  /** The one halo's state while Kory works on it; null once the reply is done. */
  halo: KoryHaloState | null;
  working: boolean;
};

function userTurnsSaying(messages: CosSessionMessage[], text: string): number[] {
  return messages.flatMap((m, i) => (m.role === "user" && m.content.trim() === text ? [i] : []));
}

/** A reply as a caption: plain words, the way Talk shows them. */
function captionText(markdown: string): string {
  return formatTranscript(stripMarkdown(markdown));
}

/**
 * A typed turn as the Talk caption shows it: your line as Sent, then Kory's reply
 * streaming in. Matched on your words, not a position, so a history reload that
 * replaces the thread keeps it; null until it shows, and once a newer turn follows it.
 */
export function typedTurn(
  messages: CosSessionMessage[],
  ask: TypedAsk,
  { sending, queued }: { sending: boolean; queued: boolean },
): TypedTurnView | null {
  const at = userTurnsSaying(messages, ask.text)[ask.prior];
  if (at === undefined) return null;
  const after = messages.slice(at + 1);
  if (after.some((m) => m.role === "user" || m.kind === "event")) return null;
  const replies = after.filter((m) => m.role === "assistant");
  const markdown = replies.map((m) => m.content).join("\n\n");
  const text = captionText(markdown);
  const working = sending || queued || replies.some((m) => m.streaming);
  // Past the stream's time budget on a turn still at work: say so, so the tray never looks dead.
  const stillWorking = working ? replies.find((m) => m.streaming && m.working)?.working : undefined;
  const heard = { text: ask.text, pending: false, sent: true };
  if (text) {
    const kory = { text, tone: working ? ("speaking" as const) : ("said" as const), reply: at };
    const turn = { status: working ? (stillWorking ? "Still working" : "Replying") : "", heard, kory };
    return { turn, at, halo: working ? "speaking" : null, working };
  }
  const hint = queued
    ? "Kory is replying elsewhere. Yours is next."
    : working
      ? (stillWorking ?? "Working on what you said.")
      : ask.stopped
        ? "Stopped. Ask again when you’re ready."
        : "Kory didn’t reply. Try asking again.";
  const kory = { text: hint, tone: "hint" as const };
  const turn = { status: working ? (stillWorking ? "Still working" : "Checking") : "", heard, kory };
  return { turn, at, halo: working ? "thinking" : null, working };
}

/** The turn the tray shows when nothing was typed this visit: the session's latest exchange. */
export type LastTurnView = {
  turn: LiveTurn;
  /** Where it starts in the thread: everything before it is the earlier conversation. */
  at: number;
  /** Kory's reply as written, for the links the plain caption drops. */
  markdown: string;
};

/** Kory's replies after `at`, up to the next thing you said, the next wake or the next day's brief, as written. */
export function replyAfter(messages: CosSessionMessage[], at: number): string {
  const replies: string[] = [];
  for (const m of messages.slice(at + 1)) {
    if (m.role === "user" || m.kind === "event" || m.kind === "brief") break;
    if (m.content.trim()) replies.push(m.content);
  }
  return replies.join("\n\n");
}

/**
 * The session's latest exchange, however it was sent (typed here, on another page, or spoken):
 * your last line and Kory's reply to it. With nothing said yet, Kory's last line on its own.
 */
export function lastTurn(messages: CosSessionMessage[], { replying = false } = {}): LastTurnView | null {
  // A day's brief opens a new turn: a line you said before it belongs to the day before.
  let at = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.kind === "brief") break;
    if (m.role === "user" && !m.kind && m.content.trim()) {
      at = i;
      break;
    }
  }
  if (at < 0) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === "assistant" && m.kind !== "event" && m.content.trim()) {
        const kory = { text: captionText(m.content), tone: "said" as const, reply: i };
        return { turn: { status: "", heard: null, kory }, at: i, markdown: m.content };
      }
    }
    return null;
  }
  const markdown = replyAfter(messages, at);
  const heard = {
    text: messages[at].content.trim(),
    pending: false,
    sent: true,
    ...(messages[at].summarized ? { summarized: true } : {}),
  };
  const kory = markdown
    ? { text: captionText(markdown), tone: "said" as const, reply: at }
    : { text: replying ? "Working on what you said." : "Kory didn’t reply. Try asking again.", tone: "hint" as const };
  return { turn: { status: replying ? "Replying" : "", heard, kory }, at, markdown };
}

/**
 * What the composer opens on: Kory's last line, from the voice call when one spoke
 * this visit, else the session's last reply. Read from what is already there, never a model call.
 */
export function openerTurn(voice: LiveTurn, messages: CosSessionMessage[]): LiveTurn | null {
  const spoken = voice.kory.tone !== "hint" ? voice.kory.text : "";
  const last = [...messages].reverse().find((m) => m.role === "assistant" && m.kind !== "event" && m.content.trim());
  const text = spoken || (last ? captionText(last.content) : "");
  // A voice answer the session wrote keeps its written form.
  const display = spoken ? voice.kory.display : undefined;
  return text ? { status: "", heard: null, kory: { text, tone: "said", reply: -1, ...(display ? { display } : {}) } } : null;
}

/**
 * The tray's typed turn: `start(text)` right before sending it, and the view follows
 * the session thread from there. `onReplied` fires once when Kory finishes a reply.
 */
export function useTypedTurn(
  messages: CosSessionMessage[],
  status: { sending: boolean; queued: boolean },
  onReplied?: () => void,
) {
  const [ask, setAsk] = useState<TypedAsk | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const start = useCallback((text: string) => {
    const value = text.trim();
    // The send is the gesture that lets the thinking rustle play later in this turn.
    if (soundsEnabled()) sharedAudioContext();
    setAsk({ text: value, prior: userTurnsSaying(messagesRef.current, value).length });
  }, []);
  const clear = useCallback(() => setAsk(null), []);
  const stop = useCallback(() => setAsk((prev) => prev && { ...prev, stopped: true }), []);
  const view = ask ? typedTurn(messages, ask, status) : null;
  const working = Boolean(view?.working);
  const replied = Boolean(view && !view.working && view.turn.kory.tone === "said");
  const onRepliedRef = useRef(onReplied);
  onRepliedRef.current = onReplied;
  const wasWorking = useRef(false);
  useEffect(() => {
    if (wasWorking.current && replied) onRepliedRef.current?.();
    wasWorking.current = working;
  }, [working, replied]);
  return { view, start, clear, stop };
}
